import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useConfig } from '../contexts/ConfigContext';

export function ProtectedRoute({ children, requireSuperAdmin = false }) {
  const {
    user,
    profile,
    initialAuthLoading,
    profileError,
    refreshProfile,
    signOut,
  } = useAuth();
  const { config, loading: configLoading } = useConfig();
  const location = useLocation();
  const hasActiveProfile = profile?.status === 'active';

  if (initialAuthLoading || (!user && configLoading)) {
    return (
      <div className="min-h-screen bg-navy-950 flex items-center justify-center" data-testid="auth-loading">
        <div className="text-slate-400 text-sm">Loading workspace...</div>
      </div>
    );
  }

  if (!user) {
    if (config?.enable_login === false) {
      return (
        <div className="min-h-screen bg-navy-950 flex items-center justify-center px-6" data-testid="auth-disabled">
          <div className="max-w-md text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-slate-300 mb-6">
              Private workspace
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-white mb-3">Login is currently disabled</h1>
            <p className="text-slate-400 leading-relaxed">
              Access is unavailable until the backend login flag is enabled again.
            </p>
          </div>
        </div>
      );
    }

    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (profileError && !hasActiveProfile) {
    if (profileError.kind === 'unprovisioned' || profileError.kind === 'disabled' || profileError.kind === 'unauthorized') {
      return <Navigate to="/login" replace state={{ from: location, authMessage: profileError.message }} />;
    }

    return (
      <div className="min-h-screen bg-navy-950 flex items-center justify-center px-6" data-testid="auth-error">
        <div className="max-w-md text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-slate-300 mb-6">
            Access check unavailable
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-white mb-3">We could not verify your workspace access</h1>
          <p className="text-slate-400 leading-relaxed">
            {profileError.message}
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <button
              onClick={() => refreshProfile()}
              className="inline-flex items-center justify-center px-5 py-3 rounded-lg bg-cyan-glow text-navy-950 font-semibold hover:bg-cyan-hover transition-colors"
            >
              Retry
            </button>
            <button
              onClick={() => signOut()}
              className="inline-flex items-center justify-center px-5 py-3 rounded-lg border border-white/10 text-white hover:bg-white/5 transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!hasActiveProfile) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (requireSuperAdmin && profile?.role !== 'super_admin') {
    return (
      <div className="min-h-screen bg-navy-950 flex items-center justify-center px-6" data-testid="forbidden">
        <div className="max-w-md text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-slate-300 mb-6">
            Restricted area
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-white mb-3">Super-admin access required</h1>
          <p className="text-slate-400 leading-relaxed">
            This is the ComplyTradeIQ Internal Operations Console. Contact your administrator for access.
          </p>
        </div>
      </div>
    );
  }

  return children;
}

export default ProtectedRoute;
